import { useEffect, useState } from "react"
import { Head, Link, router } from "@inertiajs/react"

import AppShell from "@/components/tuw/AppShell"
import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { TextField } from "@/components/tuw/auth/fields"
import CountryRegionFields, { REGIONS } from "@/components/tuw/onboarding/CountryRegionFields"
import { Button } from "@/components/ui/button"
import { track } from "@/lib/openHouse/analytics"

// Payment desk (P3-H11): an Open House project's billing address and phone,
// asked for when payment is due (guest-wording §9). Valid saved details are
// shown for reuse, with "Update billing details" to change them. Saving PATCHes
// the customer's own fields (Customer::BillingDetailsController) and, once
// saved, continues to checkout through the project's pay action, the one
// checkout path. Saving itself makes no payment.
export default function OpenHouseBilling({
  copy, values, locked, complete, billingPath, payPath, target, orderPath, errors = {},
}) {
  const words = copy.paymentDetails
  const [editing, setEditing] = useState(!complete)
  const [form, setForm] = useState(values)
  const [clientErrors, setClientErrors] = useState({})
  const [busy, setBusy] = useState(null)
  const shown = { ...errors, ...clientErrors }

  useEffect(() => track("open_house_step", { step: "payment_details" }), [])

  const set = (name) => (event) => setForm((prev) => ({ ...prev, [name]: event.target.value }))
  const startCheckout = () => {
    setBusy("checkout")
    track("open_house_checkout")
    router.post(payPath, target ? { targetKind: target.kind, targetId: target.id } : {}, { onFinish: () => setBusy(null) })
  }

  function handleSubmit(event) {
    event.preventDefault()
    const missing = {}
    if (!form.country) missing.country = words.country.required
    if (REGIONS[form.country] && !form.region) missing.region = words.region.required
    setClientErrors(missing)
    if (Object.keys(missing).length) return

    setBusy("saving")
    router.patch(billingPath, { customer: form }, {
      preserveScroll: true,
      onSuccess: startCheckout,
      onError: () => setBusy(null),
    })
  }

  const field = (name, key, props = {}) => (
    <TextField
      id={`customer_${name}`}
      name={`customer[${name}]`}
      label={words[key].label}
      value={form[name]}
      onChange={set(name)}
      error={shown[name]}
      {...props}
    />
  )

  const savedAddress = [values.address, values.address2, values.city, values.region, values.postal_code, values.country]
    .filter(Boolean)
    .join(", ")
  const anySaved = ["address", "city", "country", "postal_code", "phone_number"].some((name) => values[name])

  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col gap-4 px-4 py-6">
      <Head title={words.heading} />
      <h1 className="font-display text-[22px] font-bold">{editing ? words.heading : words.saved.heading}</h1>
      <p className="text-[14px] text-ink-2">{editing ? words.body : words.saved.body}</p>

      {!editing ? (
        <div className="flex flex-col gap-3" data-testid="saved-billing">
          <div className="rounded-[11px] border border-border p-4 text-[14px]">
            <p>{savedAddress}</p>
            <p>{values.phone_number}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={startCheckout} disabled={busy !== null}>
              {busy === "checkout" ? copy.checkout.opening : words.saved.continue}
            </Button>
            <Button variant="outline" onClick={() => setEditing(true)} disabled={busy !== null}>
              {words.saved.edit}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {!complete && anySaved && <AuthAlert tone="bad">{words.saved.incomplete}</AuthAlert>}
          {Object.keys(shown).length > 0 && <AuthAlert tone="bad">{shown.base ?? words.validationSummary}</AuthAlert>}

          <div className="grid grid-cols-2 gap-4 max-[560px]:grid-cols-1">
            {field("first_name", "firstName", { autoComplete: "given-name", required: true, disabled: locked.first_name })}
            {field("last_name", "lastName", { autoComplete: "family-name", required: true, disabled: locked.last_name })}
          </div>
          {field("address", "address", { autoComplete: "address-line1", required: true })}
          {field("address2", "address2", { autoComplete: "address-line2" })}
          <div className="grid grid-cols-2 gap-4 max-[560px]:grid-cols-1">
            <CountryRegionFields
              country={form.country}
              region={form.region}
              onCountryChange={(value) => setForm((prev) => ({ ...prev, country: value, region: "" }))}
              onRegionChange={(value) => setForm((prev) => ({ ...prev, region: value }))}
              saved={values}
              countryError={shown.country}
              regionError={shown.region}
              copy={{
                countryLabel: words.country.label,
                countryPlaceholder: words.country.placeholder,
                countrySearch: words.country.search,
                countryEmpty: words.country.noResults,
                regionLabel: words.region.label,
                regionPlaceholder: words.region.placeholder,
                regionSearch: words.region.search,
                regionEmpty: words.region.noResults,
                regionOptionalLabel: words.region.optionalLabel,
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 max-[560px]:grid-cols-1">
            {field("city", "city", { autoComplete: "address-level2", required: true })}
            {field("postal_code", "postalCode", { autoComplete: "postal-code", required: true })}
          </div>
          {field("phone_number", "phone", { type: "tel", autoComplete: "tel", required: true, hint: words.phone.help })}

          <p className="text-[12.5px] text-ink-3">{words.taxHelp}</p>
          <p className="text-[12.5px] text-ink-3">{words.noPaymentYet}</p>
          <p className="text-[12.5px] text-ink-3">{copy.checkout.taxPending}</p>

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={busy !== null}>
              {busy === "saving" ? words.saving : busy === "checkout" ? copy.checkout.opening : words.continue}
            </Button>
          </div>
        </form>
      )}

      <Link href={orderPath} className="text-[13px] font-semibold underline">
        {words.back}
      </Link>
    </div>
  )
}

// Inertia probes a layout with the raw props before the page element (as
// OrderDetail's layout notes), so the heading is read from either shape.
OpenHouseBilling.layout = (page) => {
  const props = page.props ?? page
  return (
    <AppShell active="Projects" crumb={["Projects", props.copy?.paymentDetails?.heading]}>
      {page}
    </AppShell>
  )
}
